import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Box, Link, TextField as MuiTextField, Typography } from '@mui/material';
import { spacing } from '@mui/system';
import { Celebration } from '@mui/icons-material';

import { useAuthStore, useUserMessageStore } from '../../stores/';
import { userMessages } from '../../utils/userMessages';
import Center from '../../components/common/Center';
import styled from '@emotion/styled';
import GuestGuard from '../../components/guards/GuestGuard';
import { Formik } from 'formik';
import Loader from '../../components/common/Loader';
import { confirmPassword, password } from '../../validators/passwordValidators';
import MessageAlert from '../../components/common/MessageAlert';
import SubmitButton from '../../components/common/SubmitButton';

const TextField = styled(MuiTextField)(spacing);
const Button = styled(SubmitButton)(spacing);
const Centered = styled(Typography)`
  text-align: center;
`;

const WelcomeIcon = styled(Celebration)`
  width: 60px;
  height: 60px;
  margin: 0 auto ${(props) => props.theme.spacing(3)};
`;

function SignUp() {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  const signUp = useAuthStore.use.signUp();
  const getInvite = useAuthStore.use.getInvite();
  const email = useAuthStore.use.email();
  const signOutLocal = useAuthStore.use.signOutLocal();
  const setUserMessage = useUserMessageStore.use.setUserMessage();
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);

  useEffect(() => {
    signOutLocal();
    getInvite(inviteToken!)
      .then((resp) => {
        if (resp.userExists) {
          setUserMessage(userMessages.Auth_InviteLoginExists, 'info');
          navigate('/auth/sign-in');
        }
      })
      .catch(() => {
        navigate('/error/invalid-invite');
      })
      .finally(() => setIsInitialized(true));
  }, []);

  return (
    <>
      {!isInitialized && <Loader message="One sec! Finding your invite..."></Loader>}
      {isInitialized && (
        <GuestGuard>
          <Helmet title="Sign Up" />
          <Center>
            <WelcomeIcon color="primary"></WelcomeIcon>
          </Center>
          <Typography component="h1" variant="h3" align="center" gutterBottom>
            Hello there!
          </Typography>
          <Typography component="h2" variant="subtitle1" align="center">
            Welcome to a better pool service experience
          </Typography>
          <Box mt={4}>
            <Formik
              initialValues={{
                password: '',
                confirmPassword: '',
                submit: false,
              }}
              validationSchema={Yup.object().shape({
                password: password,
                confirmPassword: confirmPassword,
              })}
              onSubmit={async (values, { setStatus, setSubmitting }) => {
                try {
                  if (!inviteToken) {
                    // noinspection ExceptionCaughtLocallyJS
                    throw new Error(userMessages.Auth_InvalidInvite);
                  }
                  const response = await signUp(inviteToken, values.password);
                  if (response.userExists) {
                    setUserMessage(userMessages.Auth_LoginExistsForUser, 'info');
                  } else {
                    setUserMessage(userMessages.SignUp_Success, 'success');
                  }
                  navigate('/auth/sign-in');
                } catch {
                  setStatus({ success: false });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <MessageAlert></MessageAlert>
                  <TextField type="text" name="email" autoComplete="email" autoFocus={true} value={email} disabled={true} fullWidth my={3} />
                  <TextField
                    type="password"
                    name="password"
                    label="Password"
                    autoComplete="new-password"
                    value={values.password}
                    inputProps={{
                      maxLength: 50,
                    }}
                    error={Boolean(touched.password && errors.password)}
                    fullWidth
                    helperText={touched.password && errors.password}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={3}
                  />
                  <TextField
                    type="password"
                    name="confirmPassword"
                    label="Confirm password"
                    autoComplete="new-password"
                    value={values.confirmPassword}
                    inputProps={{
                      maxLength: 50,
                    }}
                    error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                    fullWidth
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={3}
                  />
                  <Button my={3} text="Sign up" isSubmitting={isSubmitting}></Button>
                  <Centered>
                    Already have an account?{' '}
                    <Link to={'/auth/sign-in'} component={RouterLink}>
                      Log in
                    </Link>
                  </Centered>
                </form>
              )}
            </Formik>
          </Box>
        </GuestGuard>
      )}
    </>
  );
}

export default SignUp;
